// WelcomeHomePage.tsx — Patch 015.12. Welcome Home (client portal).
//
// The client-facing pre-occupancy checklist. Four groups (utilities,
// address change, local registrations, Heritage), each item with an action
// description, an optional link, and a checkbox the client taps to mark it
// done. Mobile-first: clients work this on a phone.
//
// Backend: GET /api/me/projects/:id/welcome-home and the completion-only
// PATCH, both project-membership gated in the client-portal Worker.

import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { AsyncBlock } from '../components/AsyncBlock'
import { ProjectHeading } from '../components/ProjectHeading'
import {
  api,
  type WelcomeHomeItem,
  type WelcomeHomeItemGroup,
  type WelcomeHomeResponse,
} from '../lib/api'
import { fmtDate } from '../lib/format'
import { useAsync } from '../lib/useAsync'

const GROUPS: ReadonlyArray<{ id: WelcomeHomeItemGroup; label: string }> = [
  { id: 'utilities', label: 'Utilities' },
  { id: 'address_change', label: 'Address change' },
  { id: 'local', label: 'Local registrations' },
  { id: 'heritage', label: 'Heritage' },
]

export function WelcomeHomePage(): JSX.Element {
  const { projectId = '' } = useParams()
  const state = useAsync<WelcomeHomeResponse>(
    () => api.welcomeHome(projectId),
    `welcome-home-${projectId}`,
  )

  // Optimistic completion overrides keyed by item id — avoids a full-page
  // reload flash on every tap.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({})
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isDone = (item: WelcomeHomeItem): boolean =>
    overrides[item.id] ?? item.completed === 1

  async function toggle(item: WelcomeHomeItem): Promise<void> {
    const next = !isDone(item)
    setBusyId(item.id)
    setError(null)
    setOverrides((o) => ({ ...o, [item.id]: next }))
    try {
      await api.completeWelcomeHomeItem(projectId, item.id, next)
    } catch (e) {
      // Revert the optimistic flip on failure.
      setOverrides((o) => ({ ...o, [item.id]: !next }))
      setError((e as Error).message || 'Could not save that change.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="page">
      <ProjectHeading projectId={projectId} title="Welcome Home" />
      <p className="page-intro">
        Your move-in checklist. Work through it a little at a time, and check
        items off as you go. Heritage can see your progress and is here if you
        get stuck.
      </p>
      {error ? (
        <div className="card empty-card" role="alert">
          <p>{error}</p>
        </div>
      ) : null}
      <AsyncBlock
        state={state}
        isEmpty={(d) => d.items.length === 0}
        emptyText="Your welcome home package is being prepared. Heritage will add items as your move-in date approaches."
      >
        {(d) => (
          <div className="welcome-home">
            {GROUPS.map(({ id, label }) => {
              const groupItems = d.items.filter((i) => i.item_group === id)
              if (groupItems.length === 0) return null
              const done = groupItems.filter(isDone).length
              return (
                <section
                  key={id}
                  className="card welcome-home-group"
                  data-testid={`welcome-home-group-${id}`}
                >
                  <header className="welcome-home-group-head">
                    <h2>{label}</h2>
                    <span className="welcome-home-progress">
                      {done} of {groupItems.length} done
                    </span>
                  </header>
                  <progress value={done} max={groupItems.length} />
                  <ul className="row-list">
                    {groupItems.map((item) => {
                      const checked = isDone(item)
                      return (
                        <li
                          key={item.id}
                          className="row welcome-home-item"
                          data-testid={`welcome-home-item-${item.id}`}
                        >
                          <label className="welcome-home-check">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={busyId === item.id}
                              onChange={() => void toggle(item)}
                              data-testid={`welcome-home-check-${item.id}`}
                            />
                            <span className="row-main">
                              <span
                                className={
                                  checked
                                    ? 'row-title welcome-home-done'
                                    : 'row-title'
                                }
                              >
                                {item.display_name}
                              </span>
                              {item.description ? (
                                <span className="row-sub">
                                  {item.description}
                                </span>
                              ) : null}
                              {item.action_url ? (
                                <a
                                  className="welcome-home-link"
                                  href={item.action_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Open link
                                </a>
                              ) : null}
                              {item.target_date ? (
                                <span className="row-sub">
                                  Target: {fmtDate(item.target_date)}
                                </span>
                              ) : null}
                            </span>
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })}
          </div>
        )}
      </AsyncBlock>
    </div>
  )
}
